import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMemo, useRef, useState } from 'react';
import { validateStaffPhone } from '@shared/phone-quality';
import {
  Loader2,
  Phone,
  Mail,
  MapPin,
  User,
  Calendar,
  Star,
  ChevronDown,
  X,
  StickyNote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SelectPipelineStage } from '../pipeline/SelectPipelineStage';
import { Textarea } from '@/components/ui/textarea';
import { usePipelineStages } from '@/hooks/usePipelineStages';
import { apiRequest } from '@/lib/queryClient';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { SearchSelect, type Customer } from './SearchSelect';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

// Define form schema based on actual database structure
const formSchema = z.object({
  id: z.number().optional(),
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().nullable(),
  // Required unless staff tick "Don't have it yet" (checked in the resolver below).
  phone: z.string().optional().nullable(),
  phoneUnknown: z.boolean().optional(),
  address: z.string().optional().nullable(),
  postcode: z.string().optional().nullable(),
  source: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  leadScore: z.number().min(0).max(10).optional().nullable(),
  lastContactDate: z.string().optional().nullable(),
  nextContactDate: z.string().optional().nullable(),
  initialContactDate: z.string().optional().nullable(),
  siteAssessmentDate: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  pipeline_stage_id: z.number().min(1, 'Pipeline stage is required'),
  pipeline_notes: z.string().optional().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

interface CustomerFormProps {
  initialData?: FormValues;
  onSuccess?: (data: any) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CustomerForm({ initialData, onSuccess, isOpen, onOpenChange }: CustomerFormProps) {
  const { stages } = usePipelineStages();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>('new');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Phone rule: a real number or an honest "Don't have it yet". The number a
  // record already holds is never re-judged, so old placeholders stay editable.
  const initialPhoneRef = useRef<string | null>(initialData?.phone ?? null);
  const resolver = useMemo(
    () =>
      zodResolver(
        formSchema.superRefine((values, ctx) => {
          const message = validateStaffPhone({
            phone: values.phone,
            phoneUnknown: values.phoneUnknown,
            initialPhone: values.id ? initialPhoneRef.current : undefined,
          });
          if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message });
        })
      ),
    []
  );

  const defaultValues: FormValues = initialData
    ? { ...initialData, phoneUnknown: !initialData.phone?.trim() }
    : {
        name: '',
        email: '',
        phone: '',
        phoneUnknown: false,
        address: '',
        postcode: '',
        source: 'Meta',
        status: 'active',
        leadScore: 5,
        lastContactDate: new Date().toISOString().split('T')[0],
        nextContactDate: '',
        notes: '',
        pipeline_stage_id: 1,
        pipeline_notes: '',
      };

  const form = useForm<FormValues>({
    resolver,
    defaultValues,
  });
  const phoneUnknown = form.watch('phoneUnknown');

  // Handle selecting an existing customer
  const handleExistingCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    initialPhoneRef.current = customer.phone ?? null;
    form.reset({
      ...defaultValues,
      id: customer.id,
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      phoneUnknown: !customer.phone?.trim(),
      address: customer.address || '',
    });
  };

  // Handle deselecting an existing customer
  const handleDeselectCustomer = () => {
    setSelectedCustomer(null);
    initialPhoneRef.current = initialData?.phone ?? null;
    form.reset(defaultValues);
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const { phoneUnknown: noPhoneYet, ...data } = values;
      const phoneValue = noPhoneYet ? null : data.phone?.trim() || null;
      const formattedData = {
        ...data,
        phone: phoneValue,
        pipeline_stage_id: Number(data.pipeline_stage_id),
        leadScore: data.leadScore !== undefined ? Number(data.leadScore) : null,
        email: data.email || null,
        address: data.address || null,
        postcode: data.postcode || null,
        notes: data.notes || null,
        pipeline_notes: data.pipeline_notes || null,
        lastContactDate: data.lastContactDate ? new Date(data.lastContactDate) : new Date(),
        nextContactDate: data.nextContactDate ? new Date(data.nextContactDate) : null,
      };

      if (data.id) {
        const { id, ...updateData } = formattedData;

        const editableUpdateData = {
          name: updateData.name,
          email: updateData.email,
          phone: updateData.phone,
          address: updateData.address,
          postcode: updateData.postcode,
          source: updateData.source,
          status: updateData.status,
          leadScore: updateData.leadScore,
          lastContactDate: updateData.lastContactDate,
          nextContactDate: updateData.nextContactDate,
          notes: updateData.notes,
          pipeline_stage_id: Number(updateData.pipeline_stage_id),
          pipeline_notes: updateData.pipeline_notes,
        };

        const apiUpdateData = selectedCustomer
          ? {
              pipeline_stage_id: editableUpdateData.pipeline_stage_id,
              ...(form.formState.dirtyFields.pipeline_notes
                ? { pipeline_notes: editableUpdateData.pipeline_notes }
                : {}),
            }
          : Object.fromEntries(
              Object.entries(editableUpdateData).filter(
                ([field]) =>
                  form.formState.dirtyFields[field as keyof FormValues] ||
                  (field === 'phone' && form.formState.dirtyFields.phoneUnknown)
              )
            );

        await apiRequest('PATCH', `/api/customers/${id}`, apiUpdateData);

        await queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
        await queryClient.refetchQueries({
          queryKey: ['/api/pipeline-stages/data'],
          type: 'active',
        });

        toast({
          title: 'Customer updated',
          description: 'Customer details have been updated successfully.',
        });
      } else {
        const apiCreateData = {
          name: formattedData.name,
          email: formattedData.email,
          phone: formattedData.phone,
          address: formattedData.address,
          postcode: formattedData.postcode,
          source: formattedData.source,
          status: formattedData.status,
          leadScore: formattedData.leadScore,
          lastContactDate: formattedData.lastContactDate,
          nextContactDate: formattedData.nextContactDate,
          notes: formattedData.notes,
          pipelineStageId: Number(formattedData.pipeline_stage_id),
          pipelineNotes: formattedData.pipeline_notes,
          additionalData: {
            contact_preferences: {
              email: !!formattedData.email,
              phone: !!formattedData.phone,
              text: false,
            },
            contactPreference: [formattedData.email && 'Email', formattedData.phone && 'Phone']
              .filter(Boolean)
              .join(', '),
          },
        };

        await apiRequest('POST', '/api/customers', apiCreateData);

        await queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
        await queryClient.refetchQueries({
          queryKey: ['/api/pipeline-stages/data'],
          type: 'active',
        });

        toast({
          title: 'Customer added',
          description: 'New customer has been added to the pipeline.',
        });
      }

      form.reset(defaultValues);
      setSelectedCustomer(null);
      onOpenChange(false);

      if (onSuccess) {
        onSuccess(formattedData);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      toast({
        title: 'Error',
        description:
          'There was a problem saving the customer. Please check the form fields and try again.',
        variant: 'destructive',
      });
    }
  };

  const isLoading = form.formState.isSubmitting;
  const isEdit = !!initialData?.id;
  const isExistingSelected = activeTab === 'existing' && selectedCustomer !== null;

  return (
    <div className="space-y-4">
      {!isEdit && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg p-4 border border-blue-200/50 dark:border-blue-800/50">
          <Tabs
            defaultValue="new"
            className="w-full"
            onValueChange={(value) => {
              setActiveTab(value);
              if (value === 'new') handleDeselectCustomer();
            }}
          >
            <TabsList className="grid w-full grid-cols-2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
              <TabsTrigger value="existing" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Use Existing Contact
              </TabsTrigger>
              <TabsTrigger value="new" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Create New Contact
              </TabsTrigger>
            </TabsList>
            <TabsContent value="existing" className="mt-4">
              <div className="space-y-3">
                {selectedCustomer ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-sm font-semibold text-blue-700 dark:text-blue-300">
                      {selectedCustomer.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-medium truncate">{selectedCustomer.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {[selectedCustomer.phone, selectedCustomer.email]
                          .filter(Boolean)
                          .join(' · ') || 'No contact details'}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-8 w-8 rounded-full hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      onClick={handleDeselectCustomer}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Find existing contact
                      </h3>
                    </div>
                    <SearchSelect onSelect={handleExistingCustomerSelect} />
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {isExistingSelected ? (
            /* ── Existing customer: only Pipeline Stage + Notes ── */
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="pipeline_stage_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pipeline Stage *
                    </FormLabel>
                    <FormControl>
                      <SelectPipelineStage
                        value={field.value?.toString() || ''}
                        onValueChange={(value) => field.onChange(parseInt(value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pipeline_notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pipeline Notes
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Notes specific to current pipeline stage..."
                        className="resize-none min-h-[80px] border-gray-300 dark:border-gray-600"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ) : (
            /* ── New customer / Edit: progressive disclosure ── */
            <div className="space-y-4">
              {/* Section 1: Contact & Pipeline — always visible */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Full Name *
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Enter customer's full name"
                            className="pl-10 h-11 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Phone Number *
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="e.g., 07911 123456"
                            className="pl-10 h-11 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                            value={phoneUnknown ? '' : (field.value ?? '')}
                            disabled={!!phoneUnknown}
                          />
                        </div>
                      </FormControl>
                      <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={!!phoneUnknown}
                          onChange={(event) => {
                            form.setValue('phoneUnknown', event.target.checked, {
                              shouldDirty: true,
                            });
                            if (event.target.checked) form.clearErrors('phone');
                          }}
                        />
                        Don't have it yet
                      </label>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email Address
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="customer@example.com"
                            className="pl-10 h-11 border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500"
                            {...field}
                            value={field.value || ''}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pipeline_stage_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Pipeline Stage *
                      </FormLabel>
                      <FormControl>
                        <SelectPipelineStage
                          value={field.value?.toString() || ''}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Section 2: Location — collapsible */}
              <Collapsible defaultOpen={true}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <MapPin className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Location
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 transition-transform group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-3 space-y-4">
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Installation Address
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                              <Input
                                placeholder="Enter the property address for solar installation"
                                className="pl-10 h-11 border-gray-300 dark:border-gray-600 focus:border-green-500 focus:ring-green-500"
                                {...field}
                                value={field.value || ''}
                              />
                            </div>
                          </FormControl>
                          <FormDescription className="text-xs text-amber-600 dark:text-amber-400">
                            Needed for solar design — add if available
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="postcode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Postcode
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., SW1A 1AA"
                                className="h-11 border-gray-300 dark:border-gray-600 focus:border-green-500 focus:ring-green-500"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="source"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Lead Source
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value || ''}
                            >
                              <FormControl>
                                <SelectTrigger className="h-11 border-gray-300 dark:border-gray-600">
                                  <SelectValue placeholder="Select lead source" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Meta">Meta (Facebook/Instagram)</SelectItem>
                                <SelectItem value="Octopus">Octopus</SelectItem>
                                <SelectItem value="Facebook">Facebook</SelectItem>
                                <SelectItem value="Instagram">Instagram</SelectItem>
                                <SelectItem value="Google">Google Ads</SelectItem>
                                <SelectItem value="Google Search">Google Search</SelectItem>
                                <SelectItem value="Website">Website Landing Page</SelectItem>
                                <SelectItem value="Referral">Referral</SelectItem>
                                <SelectItem value="Phone Inquiry">Phone Inquiry</SelectItem>
                                <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                                <SelectItem value="Exhibition">Exhibition/Event</SelectItem>
                                <SelectItem value="Leaflet">Leaflet/Flyer</SelectItem>
                                <SelectItem value="Existing Customer">Existing Customer</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Section 3: Lead Management — collapsible */}
              <Collapsible defaultOpen={isEdit}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Star className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Lead Management
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 transition-transform group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="leadScore"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Lead Score
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Star className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  placeholder="0-10"
                                  className="pl-10 h-11 border-gray-300 dark:border-gray-600 focus:border-purple-500 focus:ring-purple-500"
                                  {...field}
                                  value={field.value === null ? '' : field.value}
                                  onChange={(e) => {
                                    const value = e.target.value ? parseInt(e.target.value) : null;
                                    if (value === null || (value >= 0 && value <= 10)) {
                                      field.onChange(value);
                                    }
                                  }}
                                />
                              </div>
                            </FormControl>
                            <FormDescription className="text-xs text-gray-500">
                              Rate lead potential from 0-10
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastContactDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Last Contact
                            </FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={`h-11 pl-3 text-left font-normal border-gray-300 dark:border-gray-600 hover:border-purple-400 ${!field.value && 'text-muted-foreground'}`}
                                  >
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {field.value ? (
                                      format(new Date(field.value), 'PPP')
                                    ) : (
                                      <span>Last contact date</span>
                                    )}
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 z-[60]" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={field.value ? new Date(field.value) : undefined}
                                  onSelect={(date) =>
                                    field.onChange(date ? date.toISOString() : '')
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormDescription className="text-xs text-gray-500">
                              When customer was last contacted
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="nextContactDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Next Contact
                            </FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    className={`h-11 pl-3 text-left font-normal border-gray-300 dark:border-gray-600 hover:border-purple-400 ${!field.value && 'text-muted-foreground'}`}
                                  >
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {field.value ? (
                                      format(new Date(field.value), 'PPP')
                                    ) : (
                                      <span>Schedule next contact</span>
                                    )}
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 z-[60]" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={field.value ? new Date(field.value) : undefined}
                                  onSelect={(date) =>
                                    field.onChange(date ? date.toISOString() : '')
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormDescription className="text-xs text-gray-500">
                              When to follow up with customer
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Section 4: Notes — collapsible */}
              <Collapsible defaultOpen={isEdit}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <StickyNote className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Notes
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 transition-transform group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              General Notes
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Add any important details about this customer..."
                                className="resize-none min-h-[80px] border-gray-300 dark:border-gray-600 focus:border-orange-500 focus:ring-orange-500"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="pipeline_notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Pipeline Notes
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Notes specific to current pipeline stage..."
                                className="resize-none min-h-[80px] border-gray-300 dark:border-gray-600 focus:border-orange-500 focus:ring-orange-500"
                                {...field}
                                value={field.value || ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Sticky Footer */}
          <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 pt-4 pb-2 -mx-6 px-6 flex justify-end gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-6 py-2.5 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="domelec-blue"
              disabled={isLoading}
              className="px-6 py-2.5"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Update Lead' : isExistingSelected ? 'Add to Pipeline' : 'Add Lead'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
